import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import StaticSeo from '../components/StaticSeo.jsx';
import { hardnessBaseline, cityKey } from '../data/hardnessBaseline.js';
import { STATES_INDEX } from '../data/states/index.js';
import { hardnessKey } from '../data/water.js';

// ─────────────────────────────────────────────────────────────────────────
// THE CLEANING SCHEDULE BUILDER
// Window Washing Guide — Tool 10 (Expansion)
// ─────────────────────────────────────────────────────────────────────────
//
// Second tool in the build order to diverge structurally from the question-
// tree pattern, and the first to produce a CALENDAR rather than a verdict
// or a document. The user enters their state, optionally their city,
// property type, and workload scale; the tool synthesizes a 12-month
// cleaning schedule with recommended passes per month, the contaminants
// driving each pass, and the chemistry and equipment guidance per pass.
//
// Calendar synthesis logic:
//   1. Baseline frequency from hardness tier
//        soft / moderate  → 2 passes per year (spring + fall)
//        hard             → 4 passes per year (quarterly)
//        very-hard        → 6 passes per year (bimonthly)
//        extremely-hard   → 12 passes per year (monthly)
//   2. Property-type modifiers
//        commercial       → monthly floor (12+ regardless of hardness)
//        heritage         → bi-annual ceiling (no more than 2)
//        residential-pro  → match the hardness baseline
//        residential-diy  → hardness baseline minus one tier (DIY users
//                            often run a lighter cadence than pros)
//   3. Contaminant-driven pass insertions
//        for each regional contaminant with severity = severe and a
//        parseable season, mark every month in that season as a
//        "contaminant pass" with the contaminant name attached
//   4. Workload-scale modifies time-per-pass estimates but not cadence
//
// Output: a 12-month grid with pass intensity per month, a legend, per-
// pass detail panels for spring/summer/fall/winter, and equipment/cadence
// notes shaped by property type and workload scale.
//
// Brand: matches TintIdentifier / FrameSubstrateIdentifier / IGUSelfCheck /
// RegionalProtocolGenerator exactly on design tokens, fonts, animation
// primitives, dark mode toggle, and numbered header. Calendar visualization
// is the new domain-specific output shape for this tool, consistent with
// the s52 handoff's flexible-verdict-UI guidance.
//
// Methodology citations: USGS Water Hardness Survey (2023), EPA municipal
// CCR data, USDA-NRCS regional climate normals, IWCA service-frequency
// reference for commercial accounts, plus the regional editorial beats
// for each state's contaminant pattern.
//
// Slot indicator: "EXPANSION · 10" (s61 RESOLVED: lock to the open-ended
// EXPANSION pattern with an implicit denominator). The wind-down plan
// freezes the tool grid at 17, and this language is the canonical form
// for every tool past the original 9-tool plan.
//
// ─────────────────────────────────────────────────────────────────────────

// ─── Property types ──────────────────────────────────────────────────────
const PROPERTY_TYPES = [
  {
    id: 'residential-diy',
    label: 'Residential — DIY homeowner',
    desc: 'Your own house. You set the cadence; the tool gives you the recommended one for your region.',
  },
  {
    id: 'residential-pro',
    label: 'Residential — working cleaner',
    desc: 'Customer route. You need the chemistry, the scheduling, and a defensible answer to "how often do you come back".',
  },
  {
    id: 'commercial',
    label: 'Commercial / property management',
    desc: 'Storefronts, office buildings, or recurring contract work. Monthly minimum is the floor.',
  },
  {
    id: 'heritage',
    label: 'Heritage residential — pre-1925 stock',
    desc: 'Historic single-pane wood-sash work. Gentler cadence; conservation-grade handling on each pass.',
  },
];

// ─── Workload scales ─────────────────────────────────────────────────────
const WORKLOAD_SCALES = [
  {
    id: 'small',
    label: 'Small',
    desc: 'Under 20 panes / under 1,500 sq ft. A morning of work per pass.',
    timeFactor: 1,
  },
  {
    id: 'medium',
    label: 'Medium',
    desc: '20–50 panes / 1,500–3,500 sq ft. A full day per pass.',
    timeFactor: 2,
  },
  {
    id: 'large',
    label: 'Large',
    desc: 'Over 50 panes / over 3,500 sq ft. Multi-day per pass or split crew.',
    timeFactor: 3.5,
  },
];

// ─── Hardness tier → baseline passes per year ────────────────────────────
const BASELINE_PASSES_BY_TIER = {
  'soft': 2,
  'moderate': 2,
  'hard': 4,
  'very-hard': 6,
  'extremely-hard': 12,
};

// s73 — was a local copy of the bands using `<` where HardWaterScorer used
// `<=`. That off-by-one reached this tool's OUTPUT, not just its wording: a
// city at exactly 250 mg/L classified 'extremely-hard' and got 12 passes/yr
// off the table above, where its actual band ('very-hard') calls for 6. It hit
// 22 cities in our own dataset — 2× at 120 and 250 mg/L, 1.5× at 180. The
// table was always right; the thresholds feeding it weren't. Bands now
// come from the one place they exist.
// Total for the same reason as RegionalProtocolGenerator's — basePasses is a
// direct table index one line down.
function classifyHardness(mgL) {
  return hardnessKey(mgL) || 'extremely-hard';
}

// ─── Property-type cadence modifiers ─────────────────────────────────────
// Apply on top of the hardness baseline. Each modifier returns the final
// passes-per-year count for the property type. Modifiers can floor (raise
// to a minimum), ceiling (cap to a maximum), or shift (move the baseline
// by one tier).
function adjustedPassesPerYear(basePasses, propertyType) {
  if (propertyType === 'commercial') {
    // Commercial floors at 12 — monthly minimum on contracted work
    return Math.max(basePasses, 12);
  }
  if (propertyType === 'heritage') {
    // Heritage ceilings at 2 — bi-annual is the conservation-grade ceiling;
    // more frequent work on historic glass risks cumulative damage
    return Math.min(basePasses, 2);
  }
  if (propertyType === 'residential-diy') {
    // DIY runs lighter than pro by approximately one tier
    if (basePasses >= 12) return 6;
    if (basePasses >= 6) return 4;
    if (basePasses >= 4) return 2;
    return 2;
  }
  // residential-pro: matches the hardness baseline exactly
  return basePasses;
}

// ─── Distribute passes across months ─────────────────────────────────────
// Given N passes per year, distribute them evenly across the 12 months.
// The distribution algorithm prefers a balanced spacing — for 2 passes,
// April and October; for 4, quarterly (Mar/Jun/Sep/Dec); for 6, every
// other month starting February; for 12, every month.
function distributePasses(passesPerYear) {
  const set = new Set();
  if (passesPerYear <= 0) return set;
  if (passesPerYear >= 12) {
    for (let m = 0; m < 12; m++) set.add(m);
    return set;
  }
  const stride = 12 / passesPerYear;
  // Anchor schedules to make them feel right: 2 passes → Apr+Oct (3, 9);
  // 4 → Mar/Jun/Sep/Dec (2, 5, 8, 11); 6 → Feb/Apr/Jun/Aug/Oct/Dec
  if (passesPerYear === 2) { set.add(3); set.add(9); return set; }
  if (passesPerYear === 3) { set.add(2); set.add(6); set.add(10); return set; }
  if (passesPerYear === 4) { set.add(2); set.add(5); set.add(8); set.add(11); return set; }
  if (passesPerYear === 6) { set.add(1); set.add(3); set.add(5); set.add(7); set.add(9); set.add(11); return set; }
  // Generic stride for any other count
  for (let i = 0; i < passesPerYear; i++) {
    set.add(Math.floor(i * stride));
  }
  return set;
}

// ─── Season string → set of month indices ────────────────────────────────
// State contaminant records use a wide variety of season formats:
//   "Apr-Jun" | "April through June" | "Feb-Apr" | "May, Sep"
//   "year-round" | "year-round (peaks during onshore winds)"
//   "Jun-Nov (storm-driven)" | "March-April, occasional fall"
// This parser handles the common patterns and returns a Set of month
// indices {0-11}. Unparseable strings return an empty set.
const MONTH_MAP = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

function parseSeasonToMonths(seasonStr) {
  const months = new Set();
  if (!seasonStr) return months;
  const lower = seasonStr.toLowerCase();

  // Year-round: all 12 months
  if (lower.includes('year-round') || lower.includes('year round')) {
    for (let m = 0; m < 12; m++) months.add(m);
    return months;
  }

  // Range form: "apr-jun", "april through june", "jun-nov (storm-driven)"
  // First try the abbreviation form: Mmm-Mmm or Mmm to Mmm
  const rangeAbbrev = lower.match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s*(?:-|through|to)\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\b/);
  if (rangeAbbrev) {
    const start = MONTH_MAP[rangeAbbrev[1]];
    const end = MONTH_MAP[rangeAbbrev[2]];
    if (start <= end) {
      for (let m = start; m <= end; m++) months.add(m);
    } else {
      // Range wraps year-end (e.g. dec-feb)
      for (let m = start; m < 12; m++) months.add(m);
      for (let m = 0; m <= end; m++) months.add(m);
    }
  }

  // Individual months: "May, Sep" or "January-February" already caught
  // by range above; pick up any standalone month names not already covered
  const individualMatches = lower.matchAll(/\b(january|february|march|april|may|june|july|august|september|sept|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)\b/g);
  for (const m of individualMatches) {
    const idx = MONTH_MAP[m[1]];
    if (idx !== undefined) months.add(idx);
  }

  return months;
}

// ─── Synthesize the schedule ─────────────────────────────────────────────
// Build the 12-month schedule object. Each month carries: index, name,
// passType (one of 'none' | 'light' | 'full' | 'contaminant' | 'deep'),
// drivers (contaminants or seasonal reasons triggering the pass), and
// notes (free-text per-month commentary derived from the state record).
function synthesize({ stateRecord, citySlug, propertyType, scale }) {
  const city = citySlug && stateRecord.topCities
    ? stateRecord.topCities.find((c) => cityKey(c) === citySlug)
    : null;

  const baseline = hardnessBaseline(stateRecord, city); // s136 (#58): published figure first, never a planning value credited to a utility
  const effectiveHardness = baseline.mgL;
  const tier = classifyHardness(effectiveHardness);
  const basePasses = BASELINE_PASSES_BY_TIER[tier];
  const adjustedPasses = adjustedPassesPerYear(basePasses, propertyType);
  const baseMonths = distributePasses(adjustedPasses);

  const propertyTypeRec = PROPERTY_TYPES.find((p) => p.id === propertyType);
  const scaleRec = WORKLOAD_SCALES.find((s) => s.id === scale);

  // Build per-month entries with contaminant flags
  const months = [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthFullNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Compute, for each contaminant, the months it affects, normalizing
  // severity casing across the dataset (some records use "high" instead
  // of "severe"; we treat both as the top severity for scheduling).
  const contaminantsByMonth = {};
  for (let m = 0; m < 12; m++) contaminantsByMonth[m] = [];
  for (const c of (stateRecord.regionalContaminants || [])) {
    const sev = (c.severity || '').toLowerCase();
    const normSeverity = sev === 'high' ? 'severe' : sev;
    if (normSeverity !== 'severe' && normSeverity !== 'moderate') continue;
    const cMonths = parseSeasonToMonths(c.season);
    for (const mIdx of cMonths) {
      contaminantsByMonth[mIdx].push({ ...c, severity: normSeverity });
    }
  }

  for (let m = 0; m < 12; m++) {
    const isBaseline = baseMonths.has(m);
    const contaminants = contaminantsByMonth[m] || [];
    const hasSevereContaminant = contaminants.some((c) => c.severity === 'severe');
    const hasModerateContaminant = contaminants.some((c) => c.severity === 'moderate');

    let passType = 'none';
    if (isBaseline && hasSevereContaminant) passType = 'deep';
    else if (isBaseline) passType = 'full';
    else if (hasSevereContaminant) passType = 'contaminant';
    else if (hasModerateContaminant && isBaseline === false && adjustedPasses < 12) passType = 'light';

    months.push({
      idx: m,
      short: monthNames[m],
      full: monthFullNames[m],
      passType,
      drivers: contaminants,
      isBaseline,
    });
  }

  // Compute the total annual passes (any month with any non-'none' type)
  const totalAnnualPasses = months.filter((mo) => mo.passType !== 'none').length;

  // Group months by season for the detail panels
  const seasonMonths = {
    spring: [months[2], months[3], months[4]],
    summer: [months[5], months[6], months[7]],
    fall: [months[8], months[9], months[10]],
    winter: [months[11], months[0], months[1]],
  };

  return {
    state: stateRecord,
    city,
    effectiveHardness,
    baseline,
    tier,
    basePasses,
    adjustedPasses,
    propertyType: propertyTypeRec,
    scale: scaleRec,
    months,
    seasonMonths,
    totalAnnualPasses,
  };
}

// ─── Pass-type glyph + label library ─────────────────────────────────────
// Used by both the calendar cells and the legend.
const PASS_GLYPHS = {
  'none':         { glyph: '·',  label: 'No scheduled pass', color: 'inkFaint' },
  'light':        { glyph: '○',  label: 'Light maintenance', color: 'sage' },
  'full':         { glyph: '●',  label: 'Full cleaning pass', color: 'sienna' },
  'contaminant':  { glyph: '◐',  label: 'Contaminant-driven pass', color: 'sienna' },
  'deep':         { glyph: '★',  label: 'Deep clean — full pass plus contaminant overlap', color: 'sienna' },
};

// ─── Component ────────────────────────────────────────────────────────────
export default function CleaningScheduleBuilder() {
  // s136 (Phase 6, tools pre-filled): ?state=<slug>&city=<city-slug> opens the tool on that place, so a
  // city or state page can deep-link here. Unknown slugs are ignored (the picker starts empty).
  const [stateSlug, setStateSlug] = useState(() => {
    const v = typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('state') || '';
    return STATES_INDEX.some((s) => s.slug === v) ? v : '';
  });
  const [citySlug, setCitySlug] = useState(() => (typeof window === 'undefined' ? '' : (new URLSearchParams(window.location.search).get('city') || '').replace(/[^a-z0-9-]/g, '')));
  const [propertyType, setPropertyType] = useState('');
  const [scale, setScale] = useState('');
  const [stateRecord, setStateRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [dark, setDark] = useState(false);

  const sortedStates = useMemo(
    () => [...STATES_INDEX].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  // Dynamic state-record load
  useEffect(() => {
    if (!stateSlug) {
      setStateRecord(null);
      setCitySlug('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    import(`../data/states/${stateSlug}.js`)
      .then((mod) => {
        if (cancelled) return;
        setStateRecord(mod.default);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('Failed to load state record:', stateSlug, err);
        setStateRecord(null);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [stateSlug]);

  const cities = stateRecord?.topCities || [];
  const canGenerate = stateRecord && propertyType && scale && !loading;

  function generate() {
    if (!canGenerate) return;
    setSchedule(synthesize({
      stateRecord, citySlug, propertyType, scale,
    }));
  }

  function reset() {
    setSchedule(null);
    setStateSlug('');
    setCitySlug('');
    setPropertyType('');
    setScale('');
    setStateRecord(null);
  }

  // ─── Design tokens (identical to other tools in this family) ─────────
  const T = dark
    ? {
        bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A',
      }
    : {
        bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A',
      };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', 'Helvetica Neue', sans-serif";
  const fontMono = "'JetBrains Mono', 'Courier New', monospace";

  function tokenFor(name) { return T[name] || T.ink; }

  return (
    <>
      <StaticSeo schemaType="webApplication" />
    <div
      style={{
        minHeight: '100vh', background: T.bg, color: T.ink,
        fontFamily: fontBody,
        transition: 'background 0.3s, color 0.3s',
        padding: '32px 20px 80px',
      }}
    >
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter+Tight:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
      />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.92); }
          to   { opacity: 1; transform: scale(1); }
        }
        .reveal { animation: fadeUp 0.6s cubic-bezier(.2,.7,.3,1) both; }
        .reveal-1 { animation-delay: 0.05s; }
        .reveal-2 { animation-delay: 0.15s; }
        .reveal-3 { animation-delay: 0.25s; }
        .reveal-4 { animation-delay: 0.35s; }
        .reveal-5 { animation-delay: 0.45s; }
        .reveal-6 { animation-delay: 0.55s; }
        .reveal-7 { animation-delay: 0.65s; }
        .input-field {
          background: transparent;
          border: 1px solid ${T.rule};
          border-left: 2px solid transparent;
          padding: 18px 22px;
          color: ${T.ink};
          font-family: ${fontBody};
          font-size: 17px;
          width: 100%;
          appearance: none;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .input-field:focus { outline: none; border-left-color: ${T.sienna}; }
        .input-field:hover { border-left-color: ${T.sienna}; }
        .input-field:disabled { opacity: 0.45; cursor: not-allowed; }
        .opt-tile {
          animation: slideIn 0.4s cubic-bezier(.2,.7,.3,1) both;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .opt-tile:nth-child(1) { animation-delay: 0.05s; }
        .opt-tile:nth-child(2) { animation-delay: 0.10s; }
        .opt-tile:nth-child(3) { animation-delay: 0.15s; }
        .opt-tile:nth-child(4) { animation-delay: 0.20s; }
        .opt-tile:hover {
          background: ${T.surface};
          border-left-color: ${T.sienna} !important;
          transform: translateX(2px);
        }
        .opt-tile.selected {
          background: ${T.surface};
          border-left-color: ${T.sienna} !important;
        }
        .month-cell {
          animation: scaleIn 0.4s cubic-bezier(.2,.7,.3,1) both;
        }
        .month-cell:nth-child(1) { animation-delay: 0.05s; }
        .month-cell:nth-child(2) { animation-delay: 0.08s; }
        .month-cell:nth-child(3) { animation-delay: 0.11s; }
        .month-cell:nth-child(4) { animation-delay: 0.14s; }
        .month-cell:nth-child(5) { animation-delay: 0.17s; }
        .month-cell:nth-child(6) { animation-delay: 0.20s; }
        .month-cell:nth-child(7) { animation-delay: 0.23s; }
        .month-cell:nth-child(8) { animation-delay: 0.26s; }
        .month-cell:nth-child(9) { animation-delay: 0.29s; }
        .month-cell:nth-child(10) { animation-delay: 0.32s; }
        .month-cell:nth-child(11) { animation-delay: 0.35s; }
        .month-cell:nth-child(12) { animation-delay: 0.38s; }
        .ghost-btn:hover { color: ${T.sienna}; }
        .pill-btn:hover { background: ${T.ink}; color: ${T.bg}; }
        .generate-btn:not(:disabled):hover { background: ${T.ink}; color: ${T.bg}; }

        @media (max-width: 720px) {
          .h1-title { font-size: 36px !important; line-height: 1.05 !important; }
          .calendar-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .section-title { font-size: 26px !important; }
        }
      `}</style>

      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        {/* Header */}
        <header
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            paddingBottom: 20, borderBottom: `1px solid ${T.rule}`, marginBottom: 56,
          }}
        >
          <div style={{ fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em' }}>
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </div>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent',
              border: `1px solid ${T.rule}`,
              borderRadius: '50%',
              width: 32, height: 32, color: T.inkSoft,
              cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Title */}
        <div className="reveal reveal-1">
          <div
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 20,
            }}
          >
            § THE TOOLS &nbsp;·&nbsp; EXPANSION · 10 &nbsp;·&nbsp; CALENDAR SYNTHESIS
          </div>

          <h1
            className="h1-title"
            style={{
              fontFamily: fontDisplay, fontSize: 56, fontWeight: 400,
              lineHeight: 1.04, letterSpacing: '-0.025em',
              margin: '0 0 24px', maxWidth: 800,
            }}
          >
            The Cleaning Schedule{' '}
            <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
              Builder
            </em>
            .
          </h1>

          <p
            style={{
              fontFamily: fontDisplay, fontSize: 21, fontWeight: 300,
              lineHeight: 1.5, color: T.inkSoft,
              maxWidth: 620, margin: '0 0 12px',
            }}
          >
            Your region, your property, your workload. The tool builds a twelve-month
            cleaning calendar with the right pass cadence for your water and the seasonal
            contaminants that drive every extra trip.
          </p>

          <p
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.05em', color: T.inkFaint, marginBottom: 56,
            }}
          >
            Cadence selection derived from USGS hardness data, IWCA service-frequency reference, and the regional editorial beats.
          </p>
        </div>

        {/* ─── INPUT PHASE ─────────────────────────────────────────── */}
        {!schedule && (
          <div>
            {/* State selector */}
            <div className="reveal reveal-2" style={{ marginBottom: 28 }}>
              <label
                htmlFor="state-select"
                style={{
                  display: 'block',
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.inkFaint,
                  marginBottom: 10,
                }}
              >
                STEP 01 &nbsp;·&nbsp; YOUR STATE
              </label>
              <select
                id="state-select"
                className="input-field"
                value={stateSlug}
                onChange={(e) => {
                  setStateSlug(e.target.value);
                  setCitySlug('');
                }}
              >
                <option value="">Select a state…</option>
                {sortedStates.map((s) => (
                  <option key={s.slug} value={s.slug}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* City selector */}
            {stateSlug && (
              <div className="reveal reveal-3" style={{ marginBottom: 28 }}>
                <label
                  htmlFor="city-select"
                  style={{
                    display: 'block',
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.inkFaint,
                    marginBottom: 10,
                  }}
                >
                  STEP 02 &nbsp;·&nbsp; YOUR CITY{' '}
                  <span style={{ color: T.inkFaint, letterSpacing: '0.05em' }}>
                    — optional, refines the cadence
                  </span>
                </label>
                <select
                  id="city-select"
                  className="input-field"
                  value={citySlug}
                  onChange={(e) => setCitySlug(e.target.value)}
                  disabled={loading || !stateRecord}
                >
                  <option value="">
                    {loading
                      ? 'Loading…'
                      : `Use the ${stateRecord?.name || 'state'} default reading`}
                  </option>
                  {cities.map((c) => {
                    const slug = cityKey(c);
                    return (
                      <option key={slug} value={slug}>
                        {c.name} &nbsp;—&nbsp; {c.hardness} mg/L
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Property type */}
            {stateRecord && !loading && (
              <div className="reveal reveal-4" style={{ marginBottom: 28 }}>
                <div
                  style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.inkFaint,
                    marginBottom: 10,
                  }}
                >
                  STEP 03 &nbsp;·&nbsp; PROPERTY TYPE
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {PROPERTY_TYPES.map((pt, i) => (
                    <button
                      key={pt.id}
                      className={`opt-tile${propertyType === pt.id ? ' selected' : ''}`}
                      onClick={() => setPropertyType(pt.id)}
                      style={{
                        background: propertyType === pt.id ? T.surface : 'transparent',
                        border: `1px solid ${T.rule}`,
                        borderLeft: `2px solid ${propertyType === pt.id ? T.sienna : 'transparent'}`,
                        padding: '20px 24px',
                        cursor: 'pointer',
                        fontFamily: fontBody,
                        color: T.ink, textAlign: 'left',
                        display: 'grid',
                        gridTemplateColumns: '32px 1fr',
                        gap: 16, alignItems: 'center',
                      }}
                    >
                      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.15em', color: T.inkFaint }}>
                        № 0{i + 1}
                      </div>
                      <div>
                        <div style={{ fontFamily: fontDisplay, fontSize: 19, marginBottom: 4 }}>
                          {pt.label}
                        </div>
                        <div style={{ fontSize: 13, color: T.inkSoft, lineHeight: 1.4 }}>
                          {pt.desc}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Scale */}
            {stateRecord && propertyType && !loading && (
              <div className="reveal reveal-5" style={{ marginBottom: 40 }}>
                <div
                  style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.inkFaint,
                    marginBottom: 10,
                  }}
                >
                  STEP 04 &nbsp;·&nbsp; WORKLOAD SCALE
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {WORKLOAD_SCALES.map((sc, i) => (
                    <button
                      key={sc.id}
                      className={`opt-tile${scale === sc.id ? ' selected' : ''}`}
                      onClick={() => setScale(sc.id)}
                      style={{
                        background: scale === sc.id ? T.surface : 'transparent',
                        border: `1px solid ${T.rule}`,
                        borderLeft: `2px solid ${scale === sc.id ? T.sienna : 'transparent'}`,
                        padding: '20px 24px',
                        cursor: 'pointer',
                        fontFamily: fontBody,
                        color: T.ink, textAlign: 'left',
                        display: 'grid',
                        gridTemplateColumns: '32px 1fr',
                        gap: 16, alignItems: 'center',
                      }}
                    >
                      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.15em', color: T.inkFaint }}>
                        № 0{i + 1}
                      </div>
                      <div>
                        <div style={{ fontFamily: fontDisplay, fontSize: 19, marginBottom: 4 }}>
                          {sc.label}
                        </div>
                        <div style={{ fontSize: 13, color: T.inkSoft, lineHeight: 1.4 }}>
                          {sc.desc}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Generate */}
            {canGenerate && (
              <div className="reveal reveal-6" style={{ marginBottom: 40 }}>
                <button
                  className="generate-btn"
                  onClick={generate}
                  style={{
                    background: T.sienna,
                    color: T.bg,
                    border: 'none',
                    fontFamily: fontMono, fontSize: 12,
                    letterSpacing: '0.18em',
                    padding: '18px 36px',
                    cursor: 'pointer',
                    transition: 'background 0.2s, color 0.2s',
                  }}
                >
                  BUILD MY SCHEDULE →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─── OUTPUT PHASE — calendar + detail panels ─────────────── */}
        {schedule && (
          <div>
            {/* Header */}
            <div className="reveal reveal-1">
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 16,
                }}
              >
                ◷ SCHEDULE &nbsp;·&nbsp; {schedule.tier.replace('-', ' ').toUpperCase()} WATER &nbsp;·&nbsp; {schedule.totalAnnualPasses} PASSES/YR
              </div>

              <h2
                style={{
                  fontFamily: fontDisplay, fontSize: 44, fontWeight: 400,
                  lineHeight: 1.05, letterSpacing: '-0.025em',
                  margin: '0 0 6px',
                }}
              >
                {schedule.city ? schedule.city.name : schedule.state.name},{' '}
                <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
                  {schedule.state.abbreviation}
                </em>
              </h2>
              <div
                style={{
                  fontFamily: fontDisplay, fontStyle: 'italic',
                  fontSize: 21, color: T.sienna,
                  marginBottom: 24,
                }}
              >
                {schedule.propertyType.label.toLowerCase()} &nbsp;·&nbsp; {schedule.scale.label.toLowerCase()} scale
              </div>

              <p
                style={{
                  fontFamily: fontDisplay, fontSize: 18, fontWeight: 300,
                  lineHeight: 1.6, color: T.inkSoft,
                  maxWidth: 640, margin: '0 0 32px',
                }}
              >
                A {schedule.baseline.short} baseline produces a {schedule.adjustedPasses}-pass cadence
                {schedule.propertyType.id === 'commercial' && schedule.basePasses < 12 && ' (floored at 12 for the commercial contract minimum)'}
                {schedule.propertyType.id === 'heritage' && schedule.basePasses > 2 && ' (ceilinged at 2 for conservation-grade cadence on historic stock)'}
                {schedule.propertyType.id === 'residential-diy' && schedule.basePasses > schedule.adjustedPasses && ' (shifted down one tier for DIY cadence)'}
                . Severe regional contaminants in {schedule.totalAnnualPasses - schedule.adjustedPasses > 0 ? `${schedule.totalAnnualPasses - schedule.adjustedPasses} additional month${schedule.totalAnnualPasses - schedule.adjustedPasses > 1 ? 's' : ''}` : 'no additional months'} drive contaminant-specific passes on top of the baseline.
              </p>
            </div>

            {/* CALENDAR GRID */}
            <div
              className="reveal reveal-2"
              style={{
                background: T.surface,
                border: `1px solid ${T.rule}`,
                padding: '28px 28px',
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 16,
                }}
              >
                ◷ THE TWELVE-MONTH CALENDAR
              </div>
              <div
                className="calendar-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(6, 1fr)',
                  gap: 1,
                  background: T.rule,
                  border: `1px solid ${T.rule}`,
                }}
              >
                {schedule.months.map((mo) => {
                  const glyph = PASS_GLYPHS[mo.passType];
                  const glyphColor = tokenFor(glyph.color);
                  return (
                    <div
                      key={mo.idx}
                      className="month-cell"
                      style={{
                        background: T.surface,
                        padding: '14px 10px',
                        textAlign: 'center',
                        minHeight: 86,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: fontMono, fontSize: 10,
                          letterSpacing: '0.18em', color: T.inkFaint,
                        }}
                      >
                        {mo.short.toUpperCase()}
                      </div>
                      <div
                        style={{
                          fontFamily: fontDisplay, fontSize: 32,
                          color: glyphColor, fontWeight: 400,
                          lineHeight: 1,
                        }}
                      >
                        {glyph.glyph}
                      </div>
                      <div
                        style={{
                          fontFamily: fontMono, fontSize: 9,
                          letterSpacing: '0.12em',
                          color: mo.passType === 'none' ? T.inkFaint : T.inkSoft,
                          textTransform: 'uppercase',
                          minHeight: 14,
                        }}
                      >
                        {mo.passType === 'deep' ? 'DEEP'
                          : mo.passType === 'full' ? 'FULL'
                          : mo.passType === 'contaminant' ? 'EXTRA'
                          : mo.passType === 'light' ? 'LIGHT'
                          : ''}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: `1px solid ${T.rule}`,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                  gap: 12,
                }}
              >
                {['deep', 'full', 'contaminant', 'light', 'none'].map((key) => {
                  const g = PASS_GLYPHS[key];
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{
                        fontFamily: fontDisplay, fontSize: 18,
                        color: tokenFor(g.color),
                        minWidth: 14,
                      }}>{g.glyph}</span>
                      <span style={{
                        fontFamily: fontMono, fontSize: 10,
                        letterSpacing: '0.1em', color: T.inkSoft,
                      }}>{g.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* SEASONAL DETAIL PANELS */}
            {(['spring', 'summer', 'fall', 'winter']).map((season, i) => {
              const seasonMonths = schedule.seasonMonths[season];
              const activeMonths = seasonMonths.filter((m) => m.passType !== 'none');
              if (activeMonths.length === 0) return null;
              const calendarText = schedule.state.cleaningCalendar?.[season];
              // Collect unique contaminant names driving passes in this season
              const driverSet = new Map();
              for (const m of activeMonths) {
                for (const d of m.drivers || []) {
                  if (!driverSet.has(d.name)) driverSet.set(d.name, d);
                }
              }
              const drivers = [...driverSet.values()];
              return (
                <div
                  key={season}
                  className={`reveal reveal-${i + 3}`}
                  style={{
                    background: T.surface,
                    borderLeft: `2px solid ${T.sienna}`,
                    padding: '24px 28px',
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 11,
                      letterSpacing: '0.18em', color: T.sienna,
                      marginBottom: 8,
                    }}
                  >
                    ◐ {season.toUpperCase()} &nbsp;·&nbsp; {activeMonths.length} ACTIVE MONTH{activeMonths.length > 1 ? 'S' : ''}
                  </div>
                  <h3
                    className="section-title"
                    style={{
                      fontFamily: fontDisplay, fontSize: 26, fontWeight: 400,
                      lineHeight: 1.15, letterSpacing: '-0.02em',
                      margin: '0 0 12px',
                      color: T.ink,
                    }}
                  >
                    {activeMonths.map((m) => m.full).join(', ')}
                  </h3>
                  {calendarText && (
                    <p style={{ fontFamily: fontBody, fontSize: 14, lineHeight: 1.6, color: T.ink, margin: '0 0 12px' }}>
                      {calendarText}
                    </p>
                  )}
                  {drivers.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.rule}` }}>
                      <div style={{
                        fontFamily: fontMono, fontSize: 10,
                        letterSpacing: '0.15em', color: T.inkFaint,
                        marginBottom: 8,
                      }}>
                        DRIVERS IN THIS SEASON
                      </div>
                      {drivers.map((d, dIdx) => (
                        <div key={d.name} style={{ padding: '6px 0', borderTop: dIdx === 0 ? 'none' : `1px solid ${T.rule}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                            <span style={{ fontFamily: fontDisplay, fontSize: 15, color: T.ink }}>{d.name}</span>
                            <span style={{
                              fontFamily: fontMono, fontSize: 9,
                              letterSpacing: '0.15em',
                              color: d.severity === 'severe' ? T.sienna : T.inkFaint,
                              padding: '1px 6px',
                              border: `1px solid ${d.severity === 'severe' ? T.sienna : T.rule}`,
                            }}>{d.severity?.toUpperCase()}</span>
                          </div>
                          <div style={{ fontFamily: fontBody, fontSize: 12, color: T.inkSoft, lineHeight: 1.5 }}>
                            {d.notes}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* WORKLOAD / EQUIPMENT NOTES */}
            <div
              className="reveal reveal-7"
              style={{
                background: T.surface,
                borderLeft: `2px solid ${T.sage}`,
                padding: '24px 28px',
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sage,
                  marginBottom: 8,
                }}
              >
                ▣ WORKLOAD &nbsp;·&nbsp; {schedule.scale.label.toUpperCase()} SCALE
              </div>
              <h3
                className="section-title"
                style={{
                  fontFamily: fontDisplay, fontSize: 26, fontWeight: 400,
                  lineHeight: 1.15, letterSpacing: '-0.02em',
                  margin: '0 0 12px',
                  color: T.ink,
                }}
              >
                {schedule.scale.desc}
              </h3>
              <p style={{ fontFamily: fontBody, fontSize: 14, lineHeight: 1.6, color: T.ink, margin: '0 0 12px' }}>
                At {schedule.totalAnnualPasses} pass{schedule.totalAnnualPasses === 1 ? '' : 'es'} per year, the annual workload comes to roughly{' '}
                <strong style={{ color: T.ink }}>{schedule.totalAnnualPasses * schedule.scale.timeFactor}</strong>{' '}
                {schedule.scale.timeFactor === 1 ? 'half-days' : 'day-equivalents'} of cleaning labor.
                {schedule.propertyType.id === 'commercial' && ' Commercial contracts at this cadence are typically billed monthly with the contaminant-driven passes folded into the rate.'}
                {schedule.propertyType.id === 'residential-pro' && ' Quote the cadence as your service level; the contaminant-driven passes are part of why customers in this region pay more than your soft-water-market peers do.'}
                {schedule.propertyType.id === 'residential-diy' && ' DIY users at this scale typically split each pass across two evenings or one weekend morning, with a partner if the property is large.'}
                {schedule.propertyType.id === 'heritage' && ' Heritage residential cadence is intentionally low; the labor per pass is higher because of the conservation-grade handling.'}
              </p>
            </div>

            {/* Actions */}
            <div
              className="reveal reveal-7"
              style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16, marginBottom: 40 }}
            >
              <button
                onClick={reset}
                className="pill-btn"
                style={{
                  background: 'transparent',
                  border: `1px solid ${T.ink}`,
                  color: T.ink,
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em',
                  padding: '14px 28px', cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                ↺ BUILD ANOTHER SCHEDULE
              </button>
              <button
                onClick={() => window.print()}
                className="pill-btn"
                style={{
                  background: 'transparent',
                  border: `1px solid ${T.ink}`,
                  color: T.ink,
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em',
                  padding: '14px 28px', cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                PRINT THIS SCHEDULE →
              </button>
            </div>

            {/* Cross-link grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 0,
                borderTop: `1px solid ${T.rule}`,
                marginBottom: 40,
              }}
            >
              <Link
                to={`/states/${schedule.state.slug}`}
                className="ghost-btn"
                style={{
                  padding: '20px 0',
                  borderBottom: `1px solid ${T.rule}`,
                  textDecoration: 'none', color: T.ink,
                }}
              >
                <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.18em', color: T.sienna, marginBottom: 8 }}>
                  BY REGION
                </div>
                <div style={{ fontFamily: fontDisplay, fontSize: 17, lineHeight: 1.3, paddingRight: 16 }}>
                  Full {schedule.state.name} guide
                </div>
              </Link>
              <Link
                to="/tools/regional-protocol-generator"
                className="ghost-btn"
                style={{
                  padding: '20px 0',
                  borderBottom: `1px solid ${T.rule}`,
                  textDecoration: 'none', color: T.ink,
                }}
              >
                <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.18em', color: T.sienna, marginBottom: 8 }}>
                  THE TOOLS
                </div>
                <div style={{ fontFamily: fontDisplay, fontSize: 17, lineHeight: 1.3, paddingRight: 16 }}>
                  Regional Protocol — the chemistry on top of this schedule
                </div>
              </Link>
              <Link
                to="/tools/cost-estimator"
                className="ghost-btn"
                style={{
                  padding: '20px 0',
                  borderBottom: `1px solid ${T.rule}`,
                  textDecoration: 'none', color: T.ink,
                }}
              >
                <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.18em', color: T.sienna, marginBottom: 8 }}>
                  THE TOOLS
                </div>
                <div style={{ fontFamily: fontDisplay, fontSize: 17, lineHeight: 1.3, paddingRight: 16 }}>
                  Cost Estimator — what this cadence is worth
                </div>
              </Link>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer
          style={{
            marginTop: 40, paddingTop: 24,
            borderTop: `1px solid ${T.rule}`,
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', flexWrap: 'wrap', gap: 16,
            fontFamily: fontMono, fontSize: 11,
            color: T.inkFaint, letterSpacing: '0.1em',
          }}
        >
          <span>NO. 046 &nbsp;·&nbsp; EXPANSION · 10</span>
          <Link
            to="/tools/cleaning-schedule-builder/guide"
            style={{
              color: T.sienna,
              textDecoration: 'none',
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.1em',
            }}
          >
            READ THE METHODOLOGY →
          </Link>
        </footer>
      </div>
    </div>
    </>
  );
}
